import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, tap } from 'rxjs/operators';

import {
  CompleteRequestDigitisationAction,
  RequestDigitisationActionTypes,
} from '®shared/container-structure/edit/request-digitisation';
import { CreateOrUpdate } from '®shared/container-structure/state';
import {
  ContainerEditActionTypes,
  ContainerEditActions,
  MoveActionSources,
  MoveItemAction,
  MoveSectionAction,
} from '®shared/container-structure/state/container-edit.actions';
import {
  ContainerPersistEditActionTypes,
  PersistSuccessAction,
} from '®shared/container-structure/state/container-persist-edit.actions';

import { EventsService } from './events.service';
import {
  ListBuildingItemAction,
  ListBuildingSectionAction,
} from './list-building-action';
import {
  ListBuildingEvent,
  createListBuildingEventForItem,
  createListBuildingEventForSection,
} from './list-building-events';

/**
 * This class submits Echo events from redux actions, for use in the List Building MI Dashboard
 * We specifically target actions that are emitted after an edit has been persisted to the API,
 * so that only successfully saved changes are recorded in Echo
 *
 * All actions covered by this class originate from the List Edit page,
 * so this should only be included in ContainerStructureEditModule
 */
@Injectable()
export class ListBuildingEventsEffects {
  constructor(
    private actions$: Actions,
    private eventsService: EventsService,
  ) {}

  recordEventFromPersistAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ContainerPersistEditActionTypes.PERSIST_SUCCESS),
        map(
          (action: PersistSuccessAction) =>
            action.payload.persistAction.editAction,
        ),
        map((editAction: ContainerEditActions) =>
          getListBuildingEventForAction(editAction),
        ),
        filter(eventToRecord => (eventToRecord ? true : false)),
        tap((eventToRecord: ListBuildingEvent) => {
          this.eventsService.recordEvent(eventToRecord);
        }),
      ),
    { dispatch: false },
  );

  recordRequestDigitisationEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RequestDigitisationActionTypes.COMPLETE_REQUEST_DIGITISATION),
        tap((action: CompleteRequestDigitisationAction) => {
          const { listId, item } = action.payload;
          this.eventsService.recordEvent(
            createListBuildingEventForItem(
              listId,
              item.id,
              ListBuildingItemAction.ITEM_REQUEST_DIGITISATION,
            ),
          );
        }),
      ),
    { dispatch: false },
  );
}

function getListBuildingEventForAction(
  action: ContainerEditActions,
): ListBuildingEvent | null {
  switch (action.type) {
    case ContainerEditActionTypes.CONFIRM_CREATE_SECTION: {
      const { listId, id, to } = action.payload;
      return createListBuildingEventForSection(
        listId,
        id,
        ListBuildingSectionAction.SECTION_CREATE,
        { to },
      );
    }
    case ContainerEditActionTypes.CONFIRM_UPDATE_SECTION: {
      const { listId, id } = action.payload;
      return createListBuildingEventForSection(
        listId,
        id,
        ListBuildingSectionAction.SECTION_UPDATE,
      );
    }
    case ContainerEditActionTypes.MOVE_SECTION:
    case ContainerEditActionTypes.PASTE_SECTION: {
      const { listId, section, from, to } = action.payload;
      const source =
        action instanceof MoveSectionAction
          ? action.payload.source
          : MoveActionSources.PASTE;
      return createListBuildingEventForSection(
        listId,
        section.id,
        ListBuildingSectionAction.SECTION_MOVE,
        { source, from, to },
      );
    }
    case ContainerEditActionTypes.CONFIRM_DELETE_SECTION: {
      const { listId, section, from, descendantItems, descendantSections } =
        action.payload;
      return createListBuildingEventForSection(
        listId,
        section.id,
        ListBuildingSectionAction.SECTION_DELETE,
        {
          from,
          descendant_items: descendantItems,
          descendant_sections: descendantSections,
        },
      );
    }
    case ContainerEditActionTypes.CONFIRM_CREATE_RESOURCE_ITEM: {
      const { listId, id, source, to } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        ListBuildingItemAction.ITEM_CREATE,
        { source, to },
      );
    }
    case ContainerEditActionTypes.CONFIRM_CREATE_NOTE_ITEM: {
      const { listId, id, to } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        ListBuildingItemAction.ITEM_CREATE,
        { to },
      );
    }
    case ContainerEditActionTypes.CONFIRM_UPDATE_NOTE_ITEM: {
      const { listId, id } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        ListBuildingItemAction.ITEM_UPDATE_PARAGRAPH,
      );
    }
    case ContainerEditActionTypes.MOVE_ITEM:
    case ContainerEditActionTypes.PASTE_ITEM: {
      const { listId, item, from, to } = action.payload;
      const source =
        action instanceof MoveItemAction
          ? action.payload.source
          : MoveActionSources.PASTE;
      return createListBuildingEventForItem(
        listId,
        item.id,
        ListBuildingItemAction.ITEM_MOVE,
        { source, from, to },
      );
    }
    case ContainerEditActionTypes.CONFIRM_DELETE_ITEM: {
      const { listId, item, from } = action.payload;
      return createListBuildingEventForItem(
        listId,
        item.id,
        ListBuildingItemAction.ITEM_DELETE,
        { from },
      );
    }
    case ContainerEditActionTypes.UPDATE_RESOURCE_ITEM_IMPORTANCE: {
      const { listId, id, importance, createOrUpdate } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        createOrUpdate === CreateOrUpdate.CREATE
          ? ListBuildingItemAction.ITEM_CREATE_IMPORTANCE
          : ListBuildingItemAction.ITEM_UPDATE_IMPORTANCE,
        { importance_id: importance.id },
      );
    }
    case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_IMPORTANCE: {
      const { listId, id } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        ListBuildingItemAction.ITEM_DELETE_IMPORTANCE,
      );
    }
    case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_LIBRARY_NOTE: {
      const { listId, id, createOrUpdate } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        createOrUpdate === CreateOrUpdate.CREATE
          ? ListBuildingItemAction.ITEM_CREATE_LIBRARY_NOTE
          : ListBuildingItemAction.ITEM_UPDATE_LIBRARY_NOTE,
      );
    }
    case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_LIBRARY_NOTE: {
      const { listId, id } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        ListBuildingItemAction.ITEM_DELETE_LIBRARY_NOTE,
      );
    }
    case ContainerEditActionTypes.CONFIRM_UPDATE_RESOURCE_ITEM_STUDENT_NOTE: {
      const { listId, id, createOrUpdate } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        createOrUpdate === CreateOrUpdate.CREATE
          ? ListBuildingItemAction.ITEM_CREATE_STUDENT_NOTE
          : ListBuildingItemAction.ITEM_UPDATE_STUDENT_NOTE,
      );
    }
    case ContainerEditActionTypes.DELETE_RESOURCE_ITEM_STUDENT_NOTE: {
      const { listId, id } = action.payload;
      return createListBuildingEventForItem(
        listId,
        id,
        ListBuildingItemAction.ITEM_DELETE_STUDENT_NOTE,
      );
    }
    default: {
      return null;
    }
  }
}
